import { Check, CopyPlus, FolderPlus, Palette, PenLine, Trash2, X } from "lucide-react";
import { useEffect, useRef } from "react";

import { FOLDER_COLOR } from "@/lib/palette";
import { cn } from "@/lib/utils";
import type { FolderOut } from "@/lib/api";

import type { FileRef } from "./file-selection";

export type ContextMenuState =
  | { kind: "folder"; folder: FolderOut; x: number; y: number }
  | { kind: "files"; refs: FileRef[]; x: number; y: number }
  | null;

function Item({
  icon: Icon,
  label,
  onClick,
  danger,
  disabled,
}: {
  icon: typeof PenLine;
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[12.5px] transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-40",
        danger
          ? "text-destructive/90 hover:bg-destructive/10 hover:text-destructive"
          : "text-foreground/85 hover:bg-accent",
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      {label}
    </button>
  );
}

/**
 * The right-click menu for folders and files.
 *
 * Right-click is NOT the only way to reach any of this — every action here also
 * has a keyboard route (F2, Delete, Ctrl+X/C/V) and the folder actions also live
 * on the row's hover affordance and in the inspector. A menu that is the sole
 * home of a feature is a feature keyboard and touch users don't have.
 */
export function FileContextMenu({
  state,
  onClose,
  onRenameFolder,
  onSetColor,
  onDeleteFolder,
  onNewSubfolder,
  onRenameFile,
  onDeleteFiles,
  onCut,
  onCopy,
  onDuplicate,
  onPaste,
  canPaste,
}: {
  state: ContextMenuState;
  onClose: () => void;
  onRenameFolder: (folder: FolderOut) => void;
  onSetColor: (folder: FolderOut, color: string | null) => void;
  onDeleteFolder: (folder: FolderOut) => void;
  onNewSubfolder: (folder: FolderOut) => void;
  onRenameFile: (ref: FileRef) => void;
  onDeleteFiles: (refs: FileRef[]) => void;
  onCut: (refs: FileRef[]) => void;
  onCopy: (refs: FileRef[]) => void;
  onDuplicate: (refs: FileRef[]) => void;
  onPaste: (folder: FolderOut) => void;
  canPaste: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!state) return;
    const dismiss = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    // `capture` so a click that also lands on a row closes the menu first.
    document.addEventListener("mousedown", dismiss, true);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", dismiss, true);
      document.removeEventListener("keydown", escape);
    };
  }, [state, onClose]);

  if (!state) return null;

  // Keep the menu on screen when the row is near an edge — a menu that opens
  // half off the viewport is worse than no menu.
  const width = 208;
  const height = state.kind === "folder" ? 232 : 148;
  const x = Math.min(state.x, window.innerWidth - width - 8);
  const y = Math.min(state.y, window.innerHeight - height - 8);

  return (
    <div
      ref={ref}
      role="menu"
      style={{ left: x, top: y, width }}
      className="fixed z-[80] rounded-md border border-border bg-popover p-1 shadow-[0_24px_64px_-24px_rgba(0,0,0,0.9)]"
    >
      {state.kind === "folder" ? (
        <>
          <Item
            icon={FolderPlus}
            label="New folder inside"
            onClick={() => {
              onNewSubfolder(state.folder);
              onClose();
            }}
          />
          <Item
            icon={PenLine}
            label="Rename"
            // A category root is not a folder anyone owns — it is the taxonomy.
            disabled={state.folder.path === ""}
            onClick={() => {
              onRenameFolder(state.folder);
              onClose();
            }}
          />
          <Item
            icon={Check}
            label={canPaste ? "Paste here" : "Nothing to paste"}
            disabled={!canPaste}
            onClick={() => {
              onPaste(state.folder);
              onClose();
            }}
          />
          <div className="my-1 h-px bg-border" />
          <p className="flex items-center gap-1.5 px-2 pb-1 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
            <Palette className="h-3 w-3" /> colour
          </p>
          <div className="flex flex-wrap gap-1 px-2 pb-1.5">
            {Object.entries(FOLDER_COLOR).map(([token, value]) => (
              <button
                key={token}
                type="button"
                aria-label={token}
                title={token}
                onClick={() => {
                  onSetColor(state.folder, token);
                  onClose();
                }}
                className={cn(
                  "h-4 w-4 rounded transition-transform hover:scale-110",
                  state.folder.color === token && "ring-2 ring-ring ring-offset-1 ring-offset-popover",
                )}
                style={{ background: value }}
              />
            ))}
            <button
              type="button"
              aria-label="No colour"
              title="No colour"
              onClick={() => {
                onSetColor(state.folder, null);
                onClose();
              }}
              className="grid h-4 w-4 place-items-center rounded border border-border text-muted-foreground hover:text-foreground"
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </div>
          <div className="my-1 h-px bg-border" />
          <Item
            icon={Trash2}
            label="Delete folder"
            danger
            disabled={state.folder.path === ""}
            onClick={() => {
              onDeleteFolder(state.folder);
              onClose();
            }}
          />
        </>
      ) : (
        <>
          <Item
            icon={PenLine}
            label="Rename"
            disabled={state.refs.length !== 1}
            onClick={() => {
              if (state.refs[0]) onRenameFile(state.refs[0]);
              onClose();
            }}
          />
          <Item
            icon={Check}
            label={state.refs.length > 1 ? `Cut ${state.refs.length} files` : "Cut"}
            onClick={() => {
              onCut(state.refs);
              onClose();
            }}
          />
          <Item
            icon={Check}
            label={state.refs.length > 1 ? `Copy ${state.refs.length} files` : "Copy"}
            onClick={() => {
              onCopy(state.refs);
              onClose();
            }}
          />
          <Item
            icon={CopyPlus}
            label={
              state.refs.length > 1 ? `Duplicate ${state.refs.length} files` : "Duplicate"
            }
            onClick={() => {
              onDuplicate(state.refs);
              onClose();
            }}
          />
          <div className="my-1 h-px bg-border" />
          <Item
            icon={Trash2}
            label={state.refs.length > 1 ? `Delete ${state.refs.length} files` : "Delete"}
            danger
            onClick={() => {
              onDeleteFiles(state.refs);
              onClose();
            }}
          />
        </>
      )}
    </div>
  );
}
